<div class="flex flex-col gap-2 min-w-80 h-[50vh]">
  <div class="mt-3 flex flex-wrap items-center gap-2 p-1">
    <soft-ds-tabs size="md" [value]="mode()" (valueChange)="setMode($any($event))">
      <soft-ds-tab
        value="search"
        title="Vorhandene"
        data-testid="construction-site-mode-search"
      />
      <soft-ds-tab
        value="manual"
        title="Manuell"
        data-testid="construction-site-mode-manual"
      />
    </soft-ds-tabs>
    @if (!manualMode()) {
      <soft-ds-search-input
        class="min-w-55 flex-1"
        placeholder="Baustelle oder Ort suchen …"
        [value]="search()"
        (valueChange)="search.set($event ?? '')"
      />
    }
  </div>

  @if (manualMode()) {
    <div class="flex flex-col gap-1.5 p-1">
      <soft-ds-input
        label="Baustellenbezeichnung"
        placeholder="z. B. Wohnbau Seiersberg Haus C"
        [value]="manualName()"
        (valueChange)="manualName.set($event ?? '')"
        (keydown.enter)="onApply()"
        data-testid="construction-site-manual-input"
      />
      <span class="ds-caption text-light">
        Wird als freie Bezeichnung ohne Stammdatensatz übernommen.
      </span>
    </div>
  } @else {
    @if (!loading() && constructionSites().length === 0) {
      <p class="text-sm text-light px-1 shrink-0">Keine Baustellen gefunden.</p>
    } @else if (!loading()) {
      <p class="text-sm text-light px-1 shrink-0" data-testid="construction-site-count">
        {{ constructionSitesTotal() ?? constructionSites().length }}
        {{ constructionSitesTotal() === 1 ? 'Baustelle' : 'Baustellen' }} gefunden
      </p>
    } @else {
      <p class="text-sm text-light px-1 shrink-0">Lade Baustellen...</p>
    }

    <div
      class="flex-1 flex flex-col gap-1.5 overflow-y-auto min-h-0 p-1"
      data-testid="construction-site-list"
      (scroll)="onListScroll($event)"
    >
      @if (loading()) {
        @for (_ of skeletonRows; track $index) {
          <soft-ds-item size="sm" variant="outline" class="min-h-15.5">
            <soft-ds-item-leading>
              <soft-ds-loading-skeleton [width]="32" [height]="32" [radius]="6" />
            </soft-ds-item-leading>
            <soft-ds-item-title>
              <soft-ds-loading-skeleton [width]="160" [height]="14" [radius]="4" />
            </soft-ds-item-title>
            <soft-ds-item-description>
              <soft-ds-loading-skeleton [width]="220" [height]="12" [radius]="4" />
            </soft-ds-item-description>
            <soft-ds-item-trailing>
              <soft-ds-loading-skeleton [width]="16" [height]="16" [radius]="999" />
            </soft-ds-item-trailing>
          </soft-ds-item>
        }
      }
      @for (site of constructionSites(); track rowKey(site); let i = $index) {
        <soft-ds-item
          size="sm"
          [variant]="selectedKey() === rowKey(site) ? 'default' : 'outline'"
          [class.is-selected-row]="selectedKey() === rowKey(site)"
          [interactive]="true"
          data-testid="construction-site-row"
          (activate)="selectConstructionSite(site)"
        >
          <soft-ds-item-leading>
            @if (isNoConstructionSite(site)) {
              <span
                class="inline-flex items-center justify-center size-8 rounded-md shrink-0"
                [class]="
                  selectedKey() === rowKey(site)
                    ? 'bg-primary text-white'
                    : 'bg-surface-muted text-light'
                "
              >
                <soft-ds-icon name="fa-regular fa-ban" size="md" color="current" />
              </span>
            } @else {
              <soft-site-map-thumb class="sales-map-thumb shrink-0" [index]="i" />
            }
          </soft-ds-item-leading>
          <soft-ds-item-title>
            <span class="inline-flex items-center gap-2">
              <span>{{ rowTitle(site) }}</span>
              <span class="text-xs text-light font-normal">
                {{ rowNumberLabel(site) }}
              </span>
            </span>
          </soft-ds-item-title>

          @if (!isNoConstructionSite(site) && rowAddress(site); as address) {
            <soft-ds-item-description>
              <span class="inline-flex items-center gap-1">
                <soft-ds-icon
                  name="fa-regular fa-location-dot"
                  size="xs"
                  color="current"
                />
                {{ address }}
              </span>
            </soft-ds-item-description>
          }

          <soft-ds-item-trailing>
            <span class="inline-flex items-center gap-1 text-xs text-light">
              <soft-ds-icon name="fa-regular fa-user" size="xs" color="current" />
              {{ rowCustomerName(site) }}
            </span>
            @if (selectedKey() === rowKey(site)) {
              <soft-ds-icon name="fa-solid fa-circle-check" size="sm" color="brand" />
            } @else {
              <soft-ds-icon
                name="fa-regular fa-circle"
                size="sm"
                color="current"
                class="opacity-20"
              />
            }
          </soft-ds-item-trailing>
        </soft-ds-item>
      }
      @if (loadingMore()) {
        <soft-ds-item size="sm" variant="outline" class="min-h-15.5">
          <soft-ds-item-leading>
            <soft-ds-loading-skeleton [width]="32" [height]="32" [radius]="6" />
          </soft-ds-item-leading>
          <soft-ds-item-title>
            <soft-ds-loading-skeleton [width]="160" [height]="14" [radius]="4" />
          </soft-ds-item-title>
          <soft-ds-item-description>
            <soft-ds-loading-skeleton [width]="220" [height]="12" [radius]="4" />
          </soft-ds-item-description>
        </soft-ds-item>
      }
    </div>
  }
</div>

<ng-template #bottomTpl>
  <div class="flex w-full min-w-0 items-center justify-between gap-2 p-1">
    <div class="flex min-w-0 flex-1 items-center gap-2">
      <soft-ds-button
        variant="ghost"
        size="md"
        class="shrink-0"
        (click)="openCreateForm()"
        data-testid="construction-site-create-open"
      >
        <ng-template softSlot="leading">
          <soft-ds-icon name="fa-regular fa-plus" size="sm" color="current" />
        </ng-template>
        Neue Baustelle anlegen
      </soft-ds-button>
      <span
        class="ds-caption min-w-0 truncate leading-normal text-light"
        data-testid="construction-site-hint"
      >
        {{ footerHint() }}
      </span>
    </div>
    <div class="flex shrink-0 items-center gap-2">
      @if (isEmbedded()) {
        <soft-ds-button
          variant="secondary"
          size="md"
          (click)="create.emit(local())"
          data-testid="construction-site-step-create"
        >
          {{ '#SALES.WIZARD.CREATE_DOCUMENT' | translate }}
        </soft-ds-button>
      }
      <soft-ds-button
        variant="outline"
        size="md"
        (click)="onWithoutSite()"
        data-testid="construction-site-without"
      >
        Ohne Baustelle
      </soft-ds-button>
      <soft-ds-button
        variant="primary"
        size="md"
        [disabled]="!canApply()"
        (click)="onApply()"
        data-testid="construction-site-step-confirm"
      >
        Übernehmen
      </soft-ds-button>
    </div>
  </div>
</ng-template>
